import { useContext } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LinkBankAccountRequest } from '@cash-web/protos/squareup/franklin/app/bank_account.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useOptimisticMutationLinkBank } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { Region } from '@cash-web/shared-data-access-region-provider'
import { AccountNumber, RoutingNumber } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker } from '../BaseBlocker/BaseBlocker'
type Props = {
  buttons?: React.ReactNode[]
}

type LinkBankAccountData = {
  routing_number_1?: string
  routing_number_2?: string
  account_number: string
  account_number_verify?: string
}

// 'US','GB', 'AU', and 'CA' are the only currently supported regions
// because we're not sure what to show for RoutingNumber or AccountNumber
// for other regions.
const supportedRegions = ['US', 'GB', 'AU', 'CA'] as const
type SupportedRegion = (typeof supportedRegions)[number]
function isSupportedRegion(region: Region): region is SupportedRegion {
  return supportedRegions.includes(region as SupportedRegion)
}

// NOTE: There is no custom blocker for linking a bank account. This was created because there
// are some flows in the app that need a way to link a bank account.
export const BlockerLinkBankAccount = ({ buttons }: Props) => {
  const { t } = useTranslation()
  const profileCountry = useAuth().profile?.country_code
  const form = useForm()
  const { setError } = useContext(ErrorContext)
  const flow = useFlowContext()
  const { mutateAsync: linkBankAccount } = useOptimisticMutationLinkBank({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const { handleBlockerResponse } = useBlockerResponseHandler()

  if (!profileCountry || !isSupportedRegion(profileCountry)) {
    throw new Error(`Blocker Link Bank Account - Unsupported region: ${profileCountry}`)
  }

  const handleSubmit = async (data: LinkBankAccountData) => {
    if (data.account_number !== data.account_number_verify) {
      setError(t('Account numbers must match.', { context: 'Error text.' }))
      return
    }

    const routingNumber =
      profileCountry === 'CA' ? `0${data.routing_number_1}${data.routing_number_2}` : data.routing_number_1
    const dataToSend: LinkBankAccountRequest = {
      routing_number: routingNumber,
      account_number: data.account_number,
      transfer_token: flow.active?.transferToken,
      payment_tokens: flow.active?.paymentTokens,
    }

    await handleBlockerResponse({
      promisedBlockerUpdate: linkBankAccount(dataToSend),
      defaultError: t('We were unable to link this bank account', { context: 'error message' }),
      // The request was successful if there is an `instrument` according to the proto https://prototype.sqprod.co/#/docs/squareup.franklin.app.LinkBankAccountResponse
      isResponseSuccess: response => response.instrument != null,
    })
  }

  return (
    <BaseBlocker
      form={form}
      mainText={t('Link bank', { context: 'Title text.' })}
      secondaryText={t('Linking an external account allows you to move money in and out of your Cash App balance.', {
        context: 'secondary text for linking a bank account',
      })}
      onSubmit={handleSubmit}
      buttons={buttons}
    >
      <RoutingNumber autoFocus region={profileCountry} name="routing_number" />
      <AccountNumber
        region={profileCountry}
        name="account_number"
        label={t('Account number', { context: 'Input label.' })}
      />
      <AccountNumber
        region={profileCountry}
        name="account_number_verify"
        label={t('Verify account number', { context: 'Input label.' })}
      />
    </BaseBlocker>
  )
}
